import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/header';
import '../components/billing/payment-receipt.css';
import { paymentService } from '../apis/paymentService';
import { subscriptionService } from '../apis/subscriptionService';
import { toast } from 'react-hot-toast';
import { useTrial } from '../context/TrialContext';
import { showPaymentCompletedToastOnce } from '../utils/postPaymentNavigation';

const PROVIDER_LABELS = {
  stripe: 'Stripe',
  quickbooks: 'QuickBooks',
};

export default function PaymentReturnPage() {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const provider = searchParams.get('provider') || 'stripe';
  const navigate = useNavigate();
  const { refreshTrialStatus } = useTrial();
  const [status, setStatus] = useState('pending');
  const [amount, setAmount] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!paymentId) return;
    setChecking(true);
    try {
      const res = await paymentService.getPaymentStatus(paymentId);
      const data = res.data?.body?.data || res.data?.data || res.data;
      const payment = data?.payment;
      if (payment?.status) {
        setStatus(payment.status);
        setAmount(payment.amount);
        if (payment.status === 'completed') {
          try {
            await subscriptionService.upgradeToPremium(paymentId);
            refreshTrialStatus();
          } catch (e) {
            console.warn('Subscription upgrade:', e);
          }
          showPaymentCompletedToastOnce(paymentId);
          navigate(`/payment-receipt/${paymentId}`, { replace: true });
          return;
        }
        if (payment.status === 'failed' || payment.status === 'cancelled') {
          toast.error('Payment was not completed.');
        }
      }
    } catch (err) {
      const msg =
        err.response?.data?.body?.message ||
        err.response?.data?.error ||
        'Could not verify payment status';
      toast.error(msg);
      console.error('Status check failed:', err);
    } finally {
      setChecking(false);
      setCheckedOnce(true);
    }
  }, [paymentId, navigate, refreshTrialStatus]);

  useEffect(() => {
    if (!paymentId) return;
    checkStatus();
    // Single status check on return from QuickBooks — no auto-polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  return (
    <>
      <Header />
      <div className="pay-page-shell">
        <div className="pay-page-inner pay-return-panel text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-3">
            Payment status
          </h1>
          <p className="text-slate-600 mb-6 text-sm leading-relaxed">
            You returned from {PROVIDER_LABELS[provider] || 'checkout'}.
            {amount != null
              ? ` Amount: $${parseFloat(amount).toFixed(2)}.`
              : ' We checked your payment once.'}
          </p>
          {checking && (
            <div className="flex justify-center mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600" />
            </div>
          )}
          <p className="text-sm text-slate-500 capitalize mb-6">
            Status: <span className="font-semibold text-slate-700">{status}</span>
          </p>
          {checkedOnce && status === 'pending' && !checking && (
            <p className="text-sm text-gray-600 mb-4">
              If you just paid, wait a moment and tap Check again. Status updates when your payment provider confirms payment.
            </p>
          )}
          <button
            type="button"
            onClick={checkStatus}
            disabled={checking}
            className="vps-btn-done px-6 py-3 rounded-2xl disabled:opacity-50"
          >
            {checking ? 'Checking…' : 'Check again'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/payment')}
            className="mt-4 block mx-auto text-teal-700 hover:underline text-sm font-semibold"
          >
            Back to payment
          </button>
        </div>
      </div>
    </>
  );
}
